import { useCallback, useReducer, useEffect, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useGuard } from '@ohpen/react-authz';

import { Page, CrudMode } from '@ohpen/enso-ui';
import {
  BaseRatesList,
  BaseRateCrudForm,
  InterestRateCrudForm,
  BASE_RATES_VALUE_LIST_NAMES,
  ConfirmCrDeleteModal,
  InterestRateDelete,
  getBaseRatesFormSchema,
} from '@pmp/components';
import {
  ActionEntityType,
  BaseRateRequest,
  BaseRateResponse,
  baseRatesCreateDeleteCr,
  baseRatesValidateDelete,
  InterestRateRequest,
  interestRatesCreateIrDeleteCr,
  interestRatesValidateIrDelete,
} from '@pmp/api';
import { useValueListsStore } from '@pmp/stores';
import { useCrudDraft, usePmpFetch } from '@pmp/hooks';
import type { DraftValue } from '../../assistant/draftActions';
import { createFormFields } from '@pmp/assistant';
import { initialBaseRateState, baseRateReducer } from './baseRateReducer';
import { initialInterestRateState, interestRateReducer } from './interestRateReducer';

// What the assistant may propose: the create form's own fields, so a field it names that the form does not
// offer is refused against the form the user will be looking at.
const draftFields = createFormFields(getBaseRatesFormSchema({}, () => null, CrudMode.CREATE));

export const BaseRatesPage = (): ReactElement => {
  const [isFetching, , data, , fetchData] = usePmpFetch('baseRatesGetAllBaseRates');
  const [baseRateState, baseRateDispatch] = useReducer(baseRateReducer, initialBaseRateState);
  const [interestRateState, interestRateDispatch] = useReducer(interestRateReducer, initialInterestRateState);
  const { valueLists, getValueLists } = useValueListsStore();
  const { t } = useTranslation();

  useEffect(() => {
    getValueLists({ fetchIfMissing: BASE_RATES_VALUE_LIST_NAMES });
  }, [getValueLists]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onCreateBaseRate = useCallback(() => {
    baseRateDispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE });
  }, []);

  const { allowed: canWrite } = useGuard({ permissions: ['baseRates:update'] });

  // Field names are already checked against `draftFields`; what each value is worth is the form's own
  // validation to say, and it says it on screen.
  const openProposedBaseRate = useCallback((values: Record<string, DraftValue>) => {
    baseRateDispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE, draft: values as Partial<BaseRateRequest> });
  }, []);

  // Only for a user who may create one: the assistant proposes, and a proposal nobody could submit is a form
  // opened over a change request that would be refused.
  useCrudDraft(canWrite ? ActionEntityType.BASE_RATES : undefined, draftFields, openProposedBaseRate);

  const onView = useCallback((record: BaseRateResponse) => {
    baseRateDispatch({ type: 'openCrud', record, mode: CrudMode.VIEW });
  }, []);

  const onEditBaseRate = useCallback((record: BaseRateResponse) => {
    baseRateDispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onCloseBaseRateCrud = useCallback(() => {
    baseRateDispatch({ type: 'closeCrud' });
  }, []);

  const onDeleteBaseRate = useCallback((record: BaseRateResponse) => {
    baseRateDispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseBaseRateDeleteModal = useCallback(() => {
    baseRateDispatch({ type: 'closeDeleteModal' });
  }, []);

  const onCloseInterestRateDeleteModal = useCallback(() => {
    interestRateDispatch({ type: 'closeDeleteModal' });
  }, []);

  const onCreateInterestRate = useCallback((record: InterestRateRequest) => {
    interestRateDispatch({ type: 'openCrud', record, mode: CrudMode.CREATE });
  }, []);

  const onEditInterestRate = useCallback((record: InterestRateRequest) => {
    interestRateDispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onDeleteInterestRate = useCallback((record: InterestRateDelete) => {
    interestRateDispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseInterestRateCrud = useCallback(() => {
    interestRateDispatch({ type: 'closeCrud' });
  }, []);

  return (
    <Page>
      <Page.Title>{t('pmp.baseRates.title')}</Page.Title>
      <Page.Content>
        <BaseRatesList
          isFetching={isFetching}
          valueLists={valueLists}
          data={data ?? []}
          onCreateBaseRate={onCreateBaseRate}
          onView={onView}
          onEditBaseRate={onEditBaseRate}
          onDeleteBaseRate={onDeleteBaseRate}
          onCreateInterestRate={onCreateInterestRate}
          onEditInterestRate={onEditInterestRate}
          onDeleteInterestRate={onDeleteInterestRate}
        />
        {baseRateState.crud.open && (
          <BaseRateCrudForm
            mode={baseRateState.crud.mode}
            record={baseRateState.crud.record}
            draft={baseRateState.crud.draft}
            valueLists={valueLists}
            refreshTable={fetchData}
            onClose={onCloseBaseRateCrud}
          />
        )}
        {interestRateState.crud.open && (
          <InterestRateCrudForm
            mode={interestRateState.crud.mode}
            record={interestRateState.crud.record}
            onClose={onCloseInterestRateCrud}
            refreshTable={fetchData}
          />
        )}
        <ConfirmCrDeleteModal
          open={baseRateState.deleteModal.open}
          records={baseRateState.deleteModal.record ? [baseRateState.deleteModal.record] : []}
          idKey="code"
          nameKey="name"
          entityLabel={t('pmp.baseRates.labels.baseRate')}
          entityLabelPlural={t('pmp.baseRates.labels.baseRates')}
          getPrecheck={baseRatesValidateDelete}
          createDeleteChangeRequest={baseRatesCreateDeleteCr}
          onClose={onCloseBaseRateDeleteModal}
          refreshTable={fetchData}
        />
        <ConfirmCrDeleteModal
          open={interestRateState.deleteModal.open}
          records={interestRateState.deleteModal.record ? [interestRateState.deleteModal.record] : []}
          idKey="id"
          nameKey="name"
          entityLabel={t('pmp.baseRates.labels.interestRate')}
          entityLabelPlural={t('pmp.baseRates.labels.interestRates')}
          getPrecheck={interestRatesValidateIrDelete}
          createDeleteChangeRequest={interestRatesCreateIrDeleteCr}
          onClose={onCloseInterestRateDeleteModal}
          refreshTable={fetchData}
        />
      </Page.Content>
    </Page>
  );
};
